import React, { Component, ErrorInfo, ReactNode } from 'react';
import { Alert, AlertDescription, AlertTitle } from '@/components/ui/alert';
import { Button } from '@/components/ui/button';
import { AlertCircle, RefreshCw } from 'lucide-react';
import { logClientDiagnostic, stashClientDiagnostic } from '@/lib/client-diagnostics';

interface Props {
  children: ReactNode;
  fallback?: ReactNode;
}

interface State {
  hasError: boolean;
}

class ErrorBoundary extends Component<Props, State> {
  constructor(props: Props) {
    super(props);
    this.state = {
      hasError: false,
    };
  }

  static getDerivedStateFromError(error: Error): State {
    return {
      hasError: true,
    };
  }

  componentDidCatch(error: Error, errorInfo: ErrorInfo) {
    logClientDiagnostic('error', 'ErrorBoundary', error, errorInfo);
    stashClientDiagnostic(
      'render-error',
      {
        message: error.message,
        stack: error.stack,
        componentStack: errorInfo.componentStack,
      },
      {
        kind: 'render-error',
        message: 'A render error was captured.',
      },
    );
  }

  handleReset = () => {
    this.setState({
      hasError: false,
    });
    window.location.reload();
  };

  render() {
    if (this.state.hasError) {
      if (this.props.fallback) {
        return this.props.fallback;
      }

      // Intentionally no AppHeader here — ErrorBoundary may catch auth-context
      // or routing-layer crashes where rendering AppHeader would re-throw.
      return (
        <div className="flex flex-col min-h-screen bg-brand-paper-warm">
          <main className="flex-grow container max-w-4xl mx-auto px-4 pt-20 pb-8">
            <Alert variant="destructive" className="mb-6">
              <AlertCircle className="h-4 w-4" />
              <AlertTitle>Something went wrong</AlertTitle>
              <AlertDescription>
                An unexpected error occurred. Please try refreshing the page.
              </AlertDescription>
            </Alert>

            <div className="bg-white rounded-xl border border-black/5 shadow-sm p-6">
              <p className="font-meta text-sm text-brand-text-3">
                We saved local troubleshooting info, but it is not shown here.
              </p>
              <div className="flex gap-3 mt-6">
                <Button onClick={this.handleReset} variant="default">
                  <RefreshCw className="w-4 h-4 mr-2" />
                  Reload Page
                </Button>
                <Button onClick={() => window.history.back()} variant="outline">
                  Go Back
                </Button>
              </div>
            </div>
          </main>
        </div>
      );
    }

    return this.props.children;
  }
}

export default ErrorBoundary;
